import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Package, Tags, Truck, Users, Briefcase, Building2,
  Plus, Trash2, Loader2, LogIn, ShieldAlert, IndianRupee, TrendingUp,
  AlertTriangle, PackageX, RefreshCw, ShieldCheck, Download, Pencil, Mail,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inr } from "../lib/format";
import { TrackingTimeline } from "../components/TrackingTimeline";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "products", label: "Sarees", icon: Package },
  { id: "categories", label: "Categories", icon: Tags },
  { id: "orders", label: "Orders", icon: Truck },
  { id: "customers", label: "Customers", icon: Users },
  { id: "team", label: "Team & Payroll", icon: Briefcase },
  { id: "vendors", label: "Vendors", icon: Building2 },
];

const card = "glass rounded-xl p-5";

const esc = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function openPrint(title, bodyHtml) {
  const html = `<html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,Helvetica,sans-serif;color:#141414;padding:36px;}
    h1{font-family:Georgia,serif;margin:0 0 4px;} h3{margin:22px 0 6px;border-bottom:1px solid #eee;padding-bottom:4px;}
    .muted{color:#777;font-size:11px;letter-spacing:.06em;text-transform:uppercase;}
    table{width:100%;border-collapse:collapse;margin-top:8px;} th,td{border-bottom:1px solid #e5e5e5;padding:8px;text-align:left;font-size:13px;}
    .brand{color:#B8860B;font-weight:bold;font-family:Georgia,serif;} .box{border:2px solid #141414;padding:18px;margin-top:14px;border-radius:10px;}
    .track{font-family:monospace;font-size:22px;letter-spacing:3px;} .tot{text-align:right;margin-top:10px;font-weight:bold;}
    @media print{.noprint{display:none;}}
  </style></head><body>${bodyHtml}
  <button class="noprint" onclick="window.print()" style="margin-top:26px;padding:11px 22px;background:#B8860B;color:#fff;border:0;border-radius:8px;cursor:pointer;">Print / Save as PDF</button>
  </body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank", "width=820,height=920");
  if (!w) { toast.error("Allow pop-ups to print / save PDF"); URL.revokeObjectURL(url); return; }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("day");
  const [series, setSeries] = useState([]);
  const [courier, setCourier] = useState(null);
  const [amt, setAmt] = useState({});
  const [reportStatus, setReportStatus] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/courier/status").then((r) => setCourier(r.data)).catch(() => {});
    api.get("/admin/reports/status").then((r) => setReportStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/admin/sales?period=${period}`).then((r) => setSeries(r.data)).catch(() => {});
  }, [period]);

  if (!data) return <Spinner />;
  const max = Math.max(1, ...series.map((s) => s.revenue));

  const reload = () => api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  const restock = async (id) => { const n = Number(amt[id]) || 10; await api.post(`/admin/products/${id}/restock`, { amount: n }); toast.success(`Restocked +${n}`); reload(); };
  const exportCsv = async (kind) => {
    try {
      const res = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `tashora-${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const salesPdf = async () => {
    try {
      const [m, pr, vp] = await Promise.all([
        api.get("/admin/sales?period=month"), api.get("/admin/payroll"), api.get("/admin/vendor-payouts"),
      ]);
      const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
      const rows = m.data.map((s) => `<tr><td>${esc(s.period)}</td><td>${fmt(s.revenue)}</td><td>${esc(s.orders)}</td></tr>`).join("");
      const totalRev = m.data.reduce((n, s) => n + s.revenue, 0);
      const payroll = pr.data.map((p) => `<tr><td>${esc(p.period)}</td><td>${esc(p.employee_count)}</td><td>${fmt(p.total)}</td></tr>`).join("") || '<tr><td colspan="3" class="muted">No payroll runs</td></tr>';
      const vendors = vp.data.map((p) => `<tr><td>${esc(p.vendor_name)}</td><td>${fmt(p.amount)}</td></tr>`).join("") || '<tr><td colspan="2" class="muted">No payouts</td></tr>';
      openPrint("Tashora Sales Report", `
        <div class="brand" style="font-size:26px;">Tashora</div>
        <h1>Sales &amp; Payout Report</h1>
        <div class="muted">Generated ${new Date().toLocaleDateString()}</div>
        <h3>Monthly sales</h3>
        <table><thead><tr><th>Month</th><th>Revenue</th><th>Orders</th></tr></thead><tbody>${rows}</tbody></table>
        <div class="tot">Total revenue: ${fmt(totalRev)}</div>
        <h3>Payroll runs (simulated)</h3><table><thead><tr><th>Period</th><th>Staff</th><th>Total</th></tr></thead><tbody>${payroll}</tbody></table>
        <h3>Vendor payouts (simulated)</h3><table><thead><tr><th>Vendor</th><th>Amount</th></tr></thead><tbody>${vendors}</tbody></table>
      `);
    } catch { toast.error("Could not build report"); }
  };
  const emailReport = async () => {
    setSendingReport(true);
    try {
      const r = await api.post("/admin/reports/monthly/send", {});
      toast.success(`${r.data.label} report emailed to ${r.data.recipient}`);
      api.get("/admin/reports/status").then((res) => setReportStatus(res.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not email report");
    } finally { setSendingReport(false); }
  };

  const stats = [
    { label: "Revenue", value: inr(data.revenue), icon: IndianRupee },
    { label: "Orders", value: data.total_orders, icon: TrendingUp },
    { label: "Sarees", value: data.total_sarees, icon: Package },
    { label: "Customers", value: data.customers, icon: Users },
    { label: "In transit", value: data.in_transit, icon: Truck },
    { label: "Delivered", value: data.delivered, icon: ShieldCheck },
    { label: "Stock at hand", value: data.total_stock, icon: Package },
    { label: "Stock-outs", value: data.stock_out_count, icon: PackageX },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 justify-end" data-testid="export-toolbar">
        {["orders", "customers", "sales"].map((k) => (
          <button key={k} onClick={() => exportCsv(k)} data-testid={`export-${k}`}
            className="inline-flex items-center gap-1.5 text-xs border border-gold/30 text-gold-muted rounded-full px-3 py-1.5 hover:bg-gold/10 transition-colors capitalize">
            <Download className="w-3.5 h-3.5" /> Export {k}
          </button>
        ))}
        <button onClick={salesPdf} data-testid="sales-pdf-btn"
          className="inline-flex items-center gap-1.5 text-xs bg-gold text-obsidian rounded-full px-3 py-1.5 hover:bg-gold-light transition-colors font-semibold">
          <Download className="w-3.5 h-3.5" /> Sales PDF
        </button>
        <button onClick={emailReport} disabled={sendingReport} data-testid="email-report-btn"
          className="inline-flex items-center gap-1.5 text-xs bg-gold text-obsidian rounded-full px-3 py-1.5 hover:bg-gold-light transition-colors font-semibold disabled:opacity-60">
          <Mail className="w-3.5 h-3.5" /> {sendingReport ? "Sending…" : "Email report"}
        </button>
      </div>
      {reportStatus && (
        <div data-testid="report-status" className={`${card} flex items-start gap-3 text-sm`}>
          <Mail className={`w-5 h-5 mt-0.5 ${reportStatus.configured ? "text-green-400" : "text-amber-300"}`} />
          <div>
            <p className="text-gold-muted">
              Monthly auto-report · <span className={reportStatus.configured ? "text-green-400" : "text-amber-300"}>
                {reportStatus.configured ? "LIVE" : "Not configured"}</span>
              {reportStatus.recipient ? <span className="text-muted-foreground"> → {reportStatus.recipient}</span> : null}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{reportStatus.note}</p>
            {reportStatus.last_sent ? (
              <p className="text-[11px] text-muted-foreground mt-1">
                Last sent: {reportStatus.last_sent.label || reportStatus.last_sent.month} → {reportStatus.last_sent.recipient} ({new Date(reportStatus.last_sent.sent_at).toLocaleDateString()})
              </p>
            ) : null}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={card} data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <s.icon className="w-5 h-5 text-gold" />
            <p className="font-mono text-2xl text-gold-light mt-3">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className={`${card} lg:col-span-2`}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-xl text-gold-light">Sales by {period}</h3>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 bg-obsidian-2 border-gold/25" data-testid="sales-period"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-2 border-gold/25">
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-stretch gap-2 h-56" data-testid="sales-chart">
            {series.length === 0 ? <p className="text-sm text-muted-foreground">No sales yet</p> :
              series.map((s) => (
                <div key={s.period} className="flex-1 flex flex-col justify-end items-center gap-2 group">
                  <div className="w-full bg-gold/70 hover:bg-gold rounded-t transition-all relative min-h-[2px]"
                    style={{ height: `${Math.max(3, (s.revenue / max) * 100)}%` }}>
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-gold opacity-0 group-hover:opacity-100 whitespace-nowrap">{inr(s.revenue)}</span>
                  </div>
                  <span className="text-[9px] text-muted-foreground rotate-45 origin-left whitespace-nowrap h-4">{s.period.slice(5)}</span>
                </div>
              ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className={card}>
            <h3 className="font-serif text-lg text-gold-light flex items-center gap-2"><Truck className="w-4 h-4 text-gold" /> Courier SDK</h3>
            {courier && (
              <div className="mt-3 text-sm">
                <p className="text-gold-muted">{courier.sdk} · <span className="text-green-400">{courier.connected ? "Connected" : "Offline"}</span></p>
                <span className="inline-block text-[10px] bg-gold/15 text-gold px-2 py-0.5 rounded mt-1">{courier.mode}</span>
                <ul className="mt-3 space-y-1">
                  {courier.couriers.map((c) => (
                    <li key={c.name} className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{c.name}</span>
                      <span className={c.status === "connected" ? "text-green-400" : "text-amber-400"}>{c.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className={card}>
            <h3 className="font-serif text-lg text-gold-light flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Stock alerts</h3>
            <p className="text-xs text-muted-foreground mt-2">Out of stock ({data.stock_out_count})</p>
            {data.stock_out.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm text-red-300 py-0.5" data-testid={`alert-${p.id}`}>
                <span>• {p.name}</span>
                <span className="flex items-center gap-1">
                  <input type="number" value={amt[p.id] ?? 10} onChange={(e) => setAmt({ ...amt, [p.id]: e.target.value })} data-testid={`restock-amt-${p.id}`} className="w-12 bg-obsidian border border-gold/25 rounded px-1 py-0.5 text-[11px] text-gold-muted" />
                  <button onClick={() => restock(p.id)} data-testid={`restock-${p.id}`} className="text-[11px] bg-gold/15 text-gold rounded px-2 py-0.5 hover:bg-gold/25">Add</button>
                </span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground mt-3">Low stock ({data.low_stock_count})</p>
            {data.low_stock.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm text-amber-300 py-0.5" data-testid={`alert-${p.id}`}>
                <span>• {p.name} <span className="text-muted-foreground">({p.stock})</span></span>
                <span className="flex items-center gap-1">
                  <input type="number" value={amt[p.id] ?? 10} onChange={(e) => setAmt({ ...amt, [p.id]: e.target.value })} data-testid={`restock-amt-${p.id}`} className="w-12 bg-obsidian border border-gold/25 rounded px-1 py-0.5 text-[11px] text-gold-muted" />
                  <button onClick={() => restock(p.id)} data-testid={`restock-${p.id}`} className="text-[11px] bg-gold/15 text-gold rounded px-2 py-0.5 hover:bg-gold/25">Add</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Products ---------------- */
const emptyProduct = { name: "", category: "", fabric: "", color: "", zari: "", occasion: "Festive", price: 0, image: "", description: "", stock: 0, featured: false };

function Products() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/products/categories").then((r) => setCats(r.data)); }, []);

  const save = async () => {
    if (!form.name || !form.price || !form.category) { toast.error("Name, category and price are required"); return; }
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock), highlights: form.highlights || [] };
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Saree updated" : "Saree added");
      setOpen(false); setForm(emptyProduct); setEditing(null); load();
    } catch { toast.error("Could not save"); }
  };

  const remove = async (id) => { await api.delete(`/admin/products/${id}`); toast("Saree deleted"); load(); };
  const editRow = (p) => { setEditing(p.id); setForm({ ...emptyProduct, ...p }); setOpen(true); };
  const addNew = () => { setEditing(null); setForm({ ...emptyProduct, category: cats[0] || "" }); setOpen(true); };

  return (
    <div>
      <SectionHead title={`Sarees (${items.length})`} action={<Button onClick={addNew} data-testid="add-product-btn" className="bg-gold text-obsidian hover:bg-gold-light"><Plus className="w-4 h-4 mr-1" /> Add saree</Button>} />
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="products-table">
          <thead><tr className="text-left text-muted-foreground border-b border-gold/15">
            <th className="py-2 pr-3">Saree</th><th className="pr-3">Category</th><th className="pr-3">Price</th><th className="pr-3">Stock</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-gold/8" data-testid={`product-row-${p.id}`}>
                <td className="py-2 pr-3 flex items-center gap-3">
                  {p.image ? <img src={p.image} alt="" className="w-9 h-11 object-cover rounded gold-border" /> : <span className="w-9 h-11 rounded bg-secondary gold-border inline-block" />}
                  <span className="text-gold-muted">{p.name}</span>
                </td>
                <td className="pr-3 text-muted-foreground">{p.category}</td>
                <td className="pr-3 font-mono text-gold">{inr(p.price)}</td>
                <td className="pr-3"><span className={p.stock === 0 ? "text-red-400" : p.stock <= 5 ? "text-amber-400" : "text-gold-muted"}>{p.stock}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => editRow(p)} data-testid={`edit-product-${p.id}`} className="text-xs text-gold hover:underline mr-3">Edit</button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`} className="text-destructive"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-obsidian-2 border-gold/25 max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-serif text-gold-light">{editing ? "Edit saree" : "Add saree"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" className="col-span-2"><Input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pf-category" className="bg-obsidian border-gold/25"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-obsidian-2 border-gold/25">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Price (₹)"><Input data-testid="pf-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Stock"><Input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></Field>
            <Field label="Fabric"><Input value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} /></Field>
            <Field label="Colour"><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>
            <Field label="Image URL" className="col-span-2"><Input data-testid="pf-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></Field>
            <Field label="Description" className="col-span-2"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-product-btn" className="bg-gold text-obsidian hover:bg-gold-light">{editing ? "Save changes" : "Add saree"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Categories ---------------- */
function Categories() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/categories").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; try { await api.post("/admin/categories", { name }); setName(""); toast.success("Category added"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const remove = async (n) => { await api.delete(`/admin/categories/${encodeURIComponent(n)}`); toast("Category removed"); load(); };
  return (
    <div>
      <SectionHead title={`Categories (${items.length})`} />
      <div className={`${card} max-w-lg`}>
        <div className="flex gap-2 mb-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="category-input" className="bg-obsidian border-gold/25" />
          <Button onClick={add} data-testid="add-category-btn" className="bg-gold text-obsidian hover:bg-gold-light"><Plus className="w-4 h-4" /></Button>
        </div>
        <ul className="divide-y divide-gold/10">
          {items.map((c) => (
            <li key={c.name} className="flex items-center justify-between py-2.5" data-testid={`category-row-${c.name}`}>
              <span className="text-gold-muted">{c.name} <span className="text-xs text-muted-foreground">· {c.product_count} sarees</span></span>
              <button onClick={() => remove(c.name)} data-testid={`delete-category-${c.name}`} className="text-destructive"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------- Orders ---------------- */
const STATUS_COLORS = { pending: "text-amber-400", packed: "text-blue-300", in_transit: "text-gold", delivered: "text-green-400", cancelled: "text-red-400" };
function Orders() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [shipping, setShipping] = useState(false);
  const load = () => api.get("/admin/orders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, status, courier) => {
    try { await api.put(`/admin/orders/${id}/shipment`, { status, courier }); toast.success("Shipment updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const loadTracking = async (orderId) => {
    setTrackLoading(true); setTracking(null);
    try { const r = await api.get(`/admin/orders/${orderId}/tracking`); setTracking(r.data); }
    catch (e) { console.error("Tracking load failed:", e); }
    finally { setTrackLoading(false); }
  };
  const openDetail = async (o) => {
    setDetail(o);
    loadTracking(o.order_id);
  };
  const refreshDetail = async (orderId) => {
    try {
      const r = await api.get(`/admin/orders/${orderId}`);
      setDetail(r.data);
      loadTracking(orderId);
      load();
    } catch (e) { console.error("Could not refresh order:", e); }
  };
  const shipSR = async (o, fromDetail = false) => {
    if (fromDetail) setShipping(true);
    try {
      const r = await api.post(`/admin/orders/${o.order_id}/push-shiprocket`);
      toast.success(`Pushed to Shiprocket${r.data.awb ? " · AWB " + r.data.awb : ""}`);
      if (fromDetail) await refreshDetail(o.order_id); else load();
    } catch (e) { toast.error(e.response?.data?.detail || "Shiprocket push failed"); }
    finally { if (fromDetail) setShipping(false); }
  };
  const printLabel = (o) => {
    const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
    const a = o.address || {};
    const addrHtml = a.name ? `<div class="muted" style="margin-top:10px;">Ship to</div><div>${esc(a.name)}<br/>${esc(a.line1 || "")}<br/>${esc(a.city || "")}, ${esc(a.state || "")} ${esc(a.pincode || "")}<br/>${esc(a.phone || "")}</div>` : "";
    const items = (o.items || []).map((i) => `<tr><td>${esc(i.name)}</td><td>x${esc(i.quantity || 1)}</td><td>${fmt(i.price)}</td></tr>`).join("");
    openPrint(`Label ${o.order_id}`, `
      <div class="brand" style="font-size:24px;">Tashora</div>
      <div class="muted">Packing Slip &amp; Shipping Label</div>
      <div class="box">
        <div class="muted">Ship via</div>
        <div style="font-size:16px;margin-bottom:8px;">${esc(o.courier || "Courier")} · ${esc((o.shipment_status || "pending").replace("_", " "))}</div>
        <div class="muted">Tracking</div>
        <div class="track">${esc(o.tracking || "—")}</div>
        ${addrHtml}
        <div class="muted" style="margin-top:10px;">Order</div>
        <div>#${o.order_id.slice(-8).toUpperCase()} · ${new Date(o.created_at).toLocaleDateString()}</div>
      </div>
      <h3>Items</h3>
      <table><thead><tr><th>Item</th><th>Qty</th><th>Price</th></tr></thead><tbody>${items}</tbody></table>
      <div class="tot">Total: ${fmt(o.amount)}</div>
    `);
  };
  return (
    <div>
      <SectionHead title={`Orders (${items.length})`} />
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="orders-table">
          <thead><tr className="text-left text-muted-foreground border-b border-gold/15">
            <th className="py-2 pr-3">Order</th><th className="pr-3">Items</th><th className="pr-3">Amount</th><th className="pr-3">Courier</th><th className="pr-3">Status</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.order_id} className="border-b border-gold/8" data-testid={`order-row-${o.order_id}`}>
                <td className="py-2 pr-3 font-mono text-xs text-gold">#{o.order_id.slice(-6).toUpperCase()}{o.demo ? " ·demo" : ""}</td>
                <td className="pr-3 text-muted-foreground">{o.items?.length || 0}</td>
                <td className="pr-3 font-mono text-gold">{inr(o.amount)}</td>
                <td className="pr-3 text-muted-foreground text-xs">{o.courier || "—"}{o.tracking ? <div className="text-[10px]">{o.tracking}</div> : null}</td>
                <td className={`pr-3 font-medium ${STATUS_COLORS[o.shipment_status] || ""}`}>{(o.shipment_status || "pending").replace("_", " ")}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openDetail(o)} data-testid={`detail-${o.order_id}`} className="text-xs border border-gold/40 text-gold rounded px-2 py-1 hover:bg-gold/10">Details</button>
                    <button onClick={() => shipSR(o)} data-testid={`ship-${o.order_id}`} className="text-xs bg-gold/15 text-gold rounded px-2 py-1 hover:bg-gold/25">Ship</button>
                    <button onClick={() => printLabel(o)} data-testid={`label-${o.order_id}`} className="text-xs border border-gold/30 text-gold-muted rounded px-2 py-1 hover:bg-gold/10">Label</button>
                    {o.label_url ? <a href={o.label_url} target="_blank" rel="noopener noreferrer" data-testid={`sr-label-${o.order_id}`} className="text-xs border border-green-500/40 text-green-400 rounded px-2 py-1 hover:bg-green-500/10">PDF</a> : null}
                    <Select value={o.shipment_status || "pending"} onValueChange={(v) => update(o.order_id, v, o.courier || "Shiprocket")}>
                      <SelectTrigger className="w-32 h-8 bg-obsidian border-gold/25 text-xs" data-testid={`order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-obsidian-2 border-gold/25">
                        {Object.keys(STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setTracking(null); } }}>
        <DialogContent className="bg-obsidian-2 border-gold/20 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="order-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-gold-light">
                  Order #{detail.order_id.slice(-8).toUpperCase()}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 text-sm">
                <div className="flex flex-wrap gap-x-8 gap-y-2">
                  <div><span className="text-muted-foreground">Date</span><p className="text-gold-muted">{new Date(detail.created_at).toLocaleString()}</p></div>
                  <div><span className="text-muted-foreground">Amount</span><p className="font-mono text-gold">{inr(detail.amount)}</p></div>
                  <div><span className="text-muted-foreground">Payment</span><p className="text-gold-muted capitalize">{detail.status}{detail.gift_wrap ? " · gift-wrapped" : ""}</p></div>
                  <div><span className="text-muted-foreground">Shipment</span><p className={`capitalize ${STATUS_COLORS[detail.shipment_status] || ""}`}>{(detail.shipment_status || "pending").replace("_", " ")}</p></div>
                </div>

                {detail.address && (
                  <div className="glass rounded-lg p-4" data-testid="order-detail-address">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Ship to</p>
                    <p className="text-gold-muted">{detail.address.name}</p>
                    <p className="text-muted-foreground text-xs">{detail.address.line1}, {detail.address.city}, {detail.address.state} {detail.address.pincode}</p>
                    <p className="text-muted-foreground text-xs">{detail.address.phone}{detail.address.email ? ` · ${detail.address.email}` : ""}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Items</p>
                  <div className="space-y-2">
                    {detail.items?.map((it) => (
                      <div key={it.product_id} className="flex items-center gap-3">
                        {it.image && <img src={it.image} alt="" className="w-10 h-12 object-cover rounded" />}
                        <div className="flex-1"><p className="text-gold-muted">{it.name}</p><p className="text-xs text-muted-foreground">Qty {it.quantity} · {inr(it.price)}</p></div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 border-t border-gold/15 pt-4">
                  <button onClick={() => shipSR(detail, true)} disabled={shipping} data-testid="detail-ship-btn"
                    className="inline-flex items-center gap-1.5 bg-gold text-obsidian font-semibold text-sm rounded-full px-4 py-2 hover:bg-gold-light transition-colors disabled:opacity-60">
                    {shipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Ship via Shiprocket
                  </button>
                  <button onClick={() => printLabel(detail)} data-testid="detail-label-btn"
                    className="inline-flex items-center gap-1.5 border border-gold/30 text-gold-muted text-sm rounded-full px-4 py-2 hover:bg-gold/10">
                    <Download className="w-4 h-4" /> Packing slip
                  </button>
                  {detail.label_url && (
                    <a href={detail.label_url} target="_blank" rel="noopener noreferrer" data-testid="detail-pdf-btn"
                      className="inline-flex items-center gap-1.5 border border-green-500/40 text-green-400 text-sm rounded-full px-4 py-2 hover:bg-green-500/10">
                      <Download className="w-4 h-4" /> Shiprocket label PDF
                    </a>
                  )}
                </div>

                <div className="border-t border-gold/15 pt-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Tracking</p>
                  {trackLoading ? (
                    <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 text-gold animate-spin" /></div>
                  ) : tracking ? <TrackingTimeline data={tracking} /> : <p className="text-muted-foreground">No tracking yet.</p>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Customers (CRM) ---------------- */
function Customers() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggleAdmin = async (u) => { await api.put(`/admin/users/${u.user_id}/role`, { is_admin: !u.is_admin }); toast.success(u.is_admin ? "Admin revoked" : "Promoted to admin"); load(); };
  return (
    <div>
      <SectionHead title={`Customers (${items.length})`} />
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="customers-table">
          <thead><tr className="text-left text-muted-foreground border-b border-gold/15">
            <th className="py-2 pr-3">Customer</th><th className="pr-3">Orders</th><th className="pr-3">Spent</th><th className="pr-3">Role</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-b border-gold/8" data-testid={`customer-row-${u.user_id}`}>
                <td className="py-2 pr-3">
                  <div className="text-gold-muted">{u.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="pr-3 text-muted-foreground">{u.orders}</td>
                <td className="pr-3 font-mono text-gold">{inr(u.spent)}</td>
                <td className="pr-3">{u.is_admin ? <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded">Admin</span> : <span className="text-xs text-muted-foreground">Customer</span>}</td>
                <td className="text-right"><button onClick={() => toggleAdmin(u)} data-testid={`toggle-admin-${u.user_id}`} className="text-xs text-gold hover:underline">{u.is_admin ? "Revoke admin" : "Make admin"}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Team & Payroll ---------------- */
function Team() {
  const [emps, setEmps] = useState([]);
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", email: "", salary: 0 });
  const [open, setOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const load = () => { api.get("/admin/employees").then((r) => setEmps(r.data)); api.get("/admin/payroll").then((r) => setRuns(r.data)); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name || !form.role) { toast.error("Name and role required"); return; }
    const payload = { ...form, salary: Number(form.salary) };
    if (editingEmp) await api.put(`/admin/employees/${editingEmp}`, payload);
    else await api.post("/admin/employees", payload);
    setOpen(false); setForm({ name: "", role: "", email: "", salary: 0 }); setEditingEmp(null);
    toast.success(editingEmp ? "Employee updated" : "Employee added"); load();
  };
  const editEmp = (e) => { setEditingEmp(e.employee_id); setForm({ name: e.name, role: e.role, email: e.email || "", salary: e.salary }); setOpen(true); };
  const remove = async (id) => { await api.delete(`/admin/employees/${id}`); toast("Removed"); load(); };
  const runPayroll = async () => { const r = await api.post("/admin/payroll/run"); toast.success(`Payroll processed: ${inr(r.data.total)}`); load(); };
  const totalSalary = useMemo(() => emps.reduce((n, e) => n + (e.salary || 0), 0), [emps]);
  return (
    <div className="space-y-6">
      <SectionHead title={`Team (${emps.length})`} action={
        <div className="flex gap-2">
          <Button onClick={runPayroll} data-testid="run-payroll-btn" variant="outline" className="border-gold/40 text-gold-muted"><IndianRupee className="w-4 h-4 mr-1" /> Run payroll</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={() => { setEditingEmp(null); setForm({ name: "", role: "", email: "", salary: 0 }); }} data-testid="add-employee-btn" className="bg-gold text-obsidian hover:bg-gold-light"><Plus className="w-4 h-4 mr-1" /> Add</Button></DialogTrigger>
            <DialogContent className="bg-obsidian-2 border-gold/25">
              <DialogHeader><DialogTitle className="font-serif text-gold-light">{editingEmp ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Field label="Name"><Input data-testid="ef-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Role"><Input data-testid="ef-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></Field>
                <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Monthly salary (₹)"><Input data-testid="ef-salary" type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></Field>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-employee-btn" className="bg-gold text-obsidian hover:bg-gold-light">{editingEmp ? "Save changes" : "Add employee"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      } />
      <p className="text-xs text-amber-300/80 -mt-3"><ShieldAlert className="w-3 h-3 inline mr-1" />Payroll processing is SIMULATED — connect a payout provider to go live.</p>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="employees-table">
          <thead><tr className="text-left text-muted-foreground border-b border-gold/15"><th className="py-2 pr-3">Name</th><th className="pr-3">Role</th><th className="pr-3">Salary</th><th></th></tr></thead>
          <tbody>
            {emps.map((e) => (
              <tr key={e.employee_id} className="border-b border-gold/8" data-testid={`employee-row-${e.employee_id}`}>
                <td className="py-2 pr-3 text-gold-muted">{e.name}<div className="text-xs text-muted-foreground">{e.email}</div></td>
                <td className="pr-3 text-muted-foreground">{e.role}</td>
                <td className="pr-3 font-mono text-gold">{inr(e.salary)}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => editEmp(e)} data-testid={`edit-employee-${e.employee_id}`} className="text-gold mr-3"><Pencil className="w-4 h-4 inline" /></button>
                  <button onClick={() => remove(e.employee_id)} data-testid={`delete-employee-${e.employee_id}`} className="text-destructive"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            <tr><td className="py-2 text-muted-foreground">Monthly total</td><td></td><td className="font-mono text-gold">{inr(totalSalary)}</td><td></td></tr>
          </tbody>
        </table>
      </div>
      {runs.length > 0 && (
        <div className={card}>
          <h3 className="font-serif text-lg text-gold-light mb-3">Payroll history</h3>
          {runs.map((r) => (
            <div key={r.run_id} className="flex justify-between text-sm py-1.5 border-b border-gold/8" data-testid={`payroll-run-${r.run_id}`}>
              <span className="text-gold-muted">{r.period} · {r.employee_count} staff</span>
              <span className="font-mono text-gold">{inr(r.total)} <span className="text-green-400 text-xs">{r.status}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Vendors ---------------- */
function Vendors() {
  const [items, setItems] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [form, setForm] = useState({ name: "", contact: "", outstanding: 0 });
  const [open, setOpen] = useState(false);
  const [editingVen, setEditingVen] = useState(null);
  const load = () => { api.get("/admin/vendors").then((r) => setItems(r.data)); api.get("/admin/vendor-payouts").then((r) => setPayouts(r.data)); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const payload = { ...form, outstanding: Number(form.outstanding) };
    if (editingVen) await api.put(`/admin/vendors/${editingVen}`, payload);
    else await api.post("/admin/vendors", payload);
    setOpen(false); setForm({ name: "", contact: "", outstanding: 0 }); setEditingVen(null);
    toast.success(editingVen ? "Vendor updated" : "Vendor added"); load();
  };
  const editVen = (v) => { setEditingVen(v.vendor_id); setForm({ name: v.name, contact: v.contact || "", outstanding: v.outstanding }); setOpen(true); };
  const payout = async (v) => { const r = await api.post(`/admin/vendors/${v.vendor_id}/payout`); toast.success(`Paid ${inr(r.data.amount)} to ${v.name}`); load(); };
  const remove = async (id) => { await api.delete(`/admin/vendors/${id}`); toast("Removed"); load(); };
  return (
    <div className="space-y-6">
      <SectionHead title={`Vendors (${items.length})`} action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => { setEditingVen(null); setForm({ name: "", contact: "", outstanding: 0 }); }} data-testid="add-vendor-btn" className="bg-gold text-obsidian hover:bg-gold-light"><Plus className="w-4 h-4 mr-1" /> Add vendor</Button></DialogTrigger>
          <DialogContent className="bg-obsidian-2 border-gold/25">
            <DialogHeader><DialogTitle className="font-serif text-gold-light">{editingVen ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Name"><Input data-testid="vf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Contact"><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Field>
              <Field label="Outstanding (₹)"><Input data-testid="vf-outstanding" type="number" value={form.outstanding} onChange={(e) => setForm({ ...form, outstanding: e.target.value })} /></Field>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-vendor-btn" className="bg-gold text-obsidian hover:bg-gold-light">{editingVen ? "Save changes" : "Add vendor"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <p className="text-xs text-amber-300/80 -mt-3"><ShieldAlert className="w-3 h-3 inline mr-1" />Vendor payouts are SIMULATED — connect a payout provider to go live.</p>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="vendors-table">
          <thead><tr className="text-left text-muted-foreground border-b border-gold/15"><th className="py-2 pr-3">Vendor</th><th className="pr-3">Outstanding</th><th></th></tr></thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.vendor_id} className="border-b border-gold/8" data-testid={`vendor-row-${v.vendor_id}`}>
                <td className="py-2 pr-3 text-gold-muted">{v.name}<div className="text-xs text-muted-foreground">{v.contact}</div></td>
                <td className="pr-3 font-mono text-gold">{inr(v.outstanding)}</td>
                <td className="text-right whitespace-nowrap">
                  <button disabled={!v.outstanding} onClick={() => payout(v)} data-testid={`payout-vendor-${v.vendor_id}`} className="text-xs text-gold hover:underline mr-3 disabled:opacity-40 disabled:no-underline">Pay now</button>
                  <button onClick={() => editVen(v)} data-testid={`edit-vendor-${v.vendor_id}`} className="text-gold mr-3"><Pencil className="w-4 h-4 inline" /></button>
                  <button onClick={() => remove(v.vendor_id)} data-testid={`delete-vendor-${v.vendor_id}`} className="text-destructive"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {payouts.length > 0 && (
        <div className={card}>
          <h3 className="font-serif text-lg text-gold-light mb-3">Payout history</h3>
          {payouts.map((p) => (
            <div key={p.payout_id} className="flex justify-between text-sm py-1.5 border-b border-gold/8">
              <span className="text-gold-muted">{p.vendor_name}</span>
              <span className="font-mono text-gold">{inr(p.amount)} <span className="text-green-400 text-xs">{p.status}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Shared bits ---------------- */
const Spinner = () => <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
const SectionHead = ({ title, action }) => (
  <div className="flex items-center justify-between mb-5">
    <h2 className="font-serif text-2xl text-gold-light">{title}</h2>{action}
  </div>
);
const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="caption text-[10px] block mb-1">{label}</span>{children}</label>
);

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <Spinner />;
  if (!user) return (
    <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="admin-signin">
      <LogIn className="w-10 h-10 text-gold mx-auto" />
      <h1 className="font-serif text-3xl text-gold-light mt-5">Admin sign in</h1>
      <p className="text-muted-foreground mt-2">Sign in with an admin account to continue.</p>
      <button onClick={login} className="mt-8 bg-gold text-obsidian font-semibold px-8 py-3 rounded-full">Continue with Google</button>
    </div>
  );
  if (!user.is_admin) return (
    <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="admin-denied">
      <ShieldAlert className="w-10 h-10 text-red-400 mx-auto" />
      <h1 className="font-serif text-3xl text-gold-light mt-5">Access denied</h1>
      <p className="text-muted-foreground mt-2">Your account doesn't have admin access. Ask an existing admin to promote you.</p>
    </div>
  );

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="admin-dashboard">
      <div className="flex items-center gap-2 mb-8">
        <ShieldCheck className="w-6 h-6 text-gold" />
        <div>
          <span className="caption">Tashora Admin</span>
          <h1 className="font-serif text-3xl text-gold-light -mt-1">Control Room</h1>
        </div>
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible lg:sticky lg:top-24 self-start" data-testid="admin-nav">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === t.id ? "bg-gold text-obsidian font-semibold" : "text-gold-muted hover:bg-gold/10"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </aside>
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          {tab === "overview" && <Overview />}
          {tab === "products" && <Products />}
          {tab === "categories" && <Categories />}
          {tab === "orders" && <Orders />}
          {tab === "customers" && <Customers />}
          {tab === "team" && <Team />}
          {tab === "vendors" && <Vendors />}
        </motion.div>
      </div>
    </div>
  );
}
